import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, ArrowRight, ShieldCheck, Sparkles, Network } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import comingSoonBg from "@/assets/coming-soon-bg.jpg";

const interestSchema = z.object({
  name: z.string().trim().min(1, "Your name is required").max(200),
  email: z.string().trim().email("Enter a valid email").max(320),
  organization: z.string().trim().max(200).optional().or(z.literal("")),
});

type InterestForm = z.infer<typeof interestSchema>;

const PROOF_POINTS = [
  {
    icon: ShieldCheck,
    title: "Tracking Active and Proposed Legislation",
    body: "We track every bill that shapes housing, health, justice, climate, and education across all 67 counties to see how it applies to our clients.",
  },
  {
    icon: Sparkles,
    title: "AI Enhanced Strategic Assessment",
    body: "A strategic partner that helps you maximize visibility, scale, and reach — matching your organization to funding, legislation, and the partners that move can help move your work forward.",
  },
  {
    icon: Network,
    title: "Built In Networking",
    body: "Advocates, agencies, policymakers, and funders working the same problems, in the same places creates a web of opportunities and helps remove redundancies in effort.",
  },
];

const ComingSoon = () => {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<InterestForm>({
    resolver: zodResolver(interestSchema),
    defaultValues: { name: "", email: "", organization: "" },
  });

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "The Emerging Market — Private Beta";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const onSubmit = async (values: InterestForm) => {
    const { error } = await supabase.from("signups").insert({
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      organization: values.organization?.trim() || null,
    });
    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }
    setSubmitted(true);
    form.reset();
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground flex flex-col overflow-hidden">
      {/* Background flourishes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: `url(${comingSoonBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background" />
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-3">
          <span className="font-display text-lg font-bold tracking-tight">
            The Emerging Market
          </span>
          <span className="hidden rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary sm:inline">
            Private Beta
          </span>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/auth">Member sign in</Link>
        </Button>
      </header>

      {/* Hero + form */}
      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col items-center px-6 py-12 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid w-full grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center"
        >
          {/* Left: copy */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Alabama-first · Building in public
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Powering the work that{" "}
              <span className="text-gradient-primary">moves communities</span>{" "}
              forward.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Advocating for the changes our clients want to see in the world
              starts with better understanding their stakes and positioning. So
              we invite you to join the Emerging Market, create a free profile,
              share ideas and get access to our Grants Coach, Research and
              Insights to help you build and a partner ecosystem that can help
              you grow.
            </p>

            <ul className="mt-10 space-y-6">
              {PROOF_POINTS.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-card text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display font-semibold">{title}</p>
                    <p className="text-sm text-muted-foreground">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form card */}
          <div className="relative">
            <div className="rounded-lg border border-border bg-card/80 p-6 shadow-elegant backdrop-blur sm:p-8">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-6 text-center"
                >
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <h2 className="font-display text-xl font-semibold">
                    You're on the list.
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Thanks for raising your hand. We'll be in touch as we open
                    seats. Already invited?{" "}
                    <Link to="/auth" className="text-primary hover:underline">
                      Sign in here.
                    </Link>
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-6"
                    onClick={() => setSubmitted(false)}
                  >
                    Submit another
                  </Button>
                </motion.div>
              ) : (
                <>
                  <h2 className="font-display text-2xl font-semibold">
                    Request early access
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Tell us who you are and we'll loop you in as the beta opens
                    to your stakeholder group.
                  </p>

                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="mt-6 space-y-4"
                    noValidate
                  >
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        autoComplete="name"
                        placeholder="Jane Doe"
                        {...form.register("name")}
                      />
                      {form.formState.errors.name && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.name.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@organization.org"
                        {...form.register("email")}
                      />
                      {form.formState.errors.email && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">
                        Organization{" "}
                        <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <Input
                        id="organization"
                        autoComplete="organization"
                        placeholder="Your nonprofit, agency, or firm"
                        {...form.register("organization")}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Request access
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                    <p className="text-center text-[11px] text-muted-foreground">
                      We'll only use your details to coordinate beta access.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </main>

      <footer className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-8 pt-12 text-xs text-muted-foreground">
        <div className="flex flex-col items-start justify-between gap-3 border-t border-border pt-6 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} The Emerging Market · Alabama</p>
          <div className="flex items-center gap-4">
            <a
              href="mailto:info@emrkt.co"
              className="hover:text-foreground transition-colors"
            >
              info@emrkt.co
            </a>
            <Link to="/status" className="hover:text-foreground transition-colors">
              System status
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoon;